import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Reveal, SectionHeading, FloatingDecor, Sprout, Bubbles, PageMeta } from "./ThtParts";
import { InfoTable } from "./thtData";
import "./ThtTop.css";
import "./ThtDesign.css";
import "./ThtBusiness.css";
import "./ThtContact.css";
import "./ThtNatural.css";


// ---- サイトのデザインで作ったフォーム(送信先は既存のGoogleフォーム) ----
const FORM_ID = "1FAIpQLSe4lOpmAxuMDTTsMpX_bCOM_RbeQev8YxCk9Y4BA-KfHxsMQw";
const SUBMIT_URL = `https://docs.google.com/forms/d/e/${FORM_ID}/formResponse`;
const ENTRY = {
  type: "entry.2015123027",
  name: "entry.1521672200",
  email: "entry.1700443098",
  tel: "entry.750854737",
  message: "entry.847729695",
  agree: "entry.673553895",
};
const TYPES = [
  "初回レッスン、教室に関するお問い合わせはこちら",
  "FC加盟検討や採用に関するお問い合わせはこちら",
  "イベント出演に関するお問い合わせはこちら",
];
const OTHER = "その他";

type FormState = { type: string; other: string; name: string; email: string; tel: string; message: string; agree: boolean };
const emptyForm: FormState = { type: "", other: "", name: "", email: "", tel: "", message: "", agree: false };

// プライバシーポリシーを見に行って戻ったときに入力内容が消えないよう、ブラウザに一時保存する
// (タブを閉じると消える sessionStorage を使い、送信が完了したら削除する)
const DRAFT_KEY = "tht-contact-draft";
const loadDraft = (): FormState => {
  try {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    return saved ? { ...emptyForm, ...JSON.parse(saved) } : emptyForm;
  } catch {
    return emptyForm;
  }
};
const saveDraft = (v: FormState) => {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(v));
  } catch {
    // 保存できない環境(プライベートブラウズなど)では何もしない
  }
};
const clearDraft = () => {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // 何もしない
  }
};

const validate = (v: FormState) => {
  const e: Partial<Record<keyof FormState, string>> = {};
  if (!v.type) e.type = "お問い合わせの種類を選んでください";
  if (v.type === OTHER && !v.other.trim()) e.other = "内容を入力してください";
  if (!v.name.trim()) e.name = "お名前を入力してください";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "メールアドレスを正しく入力してください";
  if (!/^[0-9０-９+\-－ ()（）]{10,}$/.test(v.tel)) e.tel = "電話番号を正しく入力してください";
  if (!v.message.trim()) e.message = "お問い合わせ内容を入力してください";
  if (!v.agree) e.agree = "プライバシーポリシーへの同意が必要です";
  return e;
};

const ContactForm = () => {
  const [values, setValues] = useState<FormState>(loadDraft);

  useEffect(() => {
    saveDraft(values);
  }, [values]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  // 入力 → 確認 → 送信中 → 完了(元のGoogleフォームと同じく、確認画面をはさむ)
  const [step, setStep] = useState<"input" | "confirm" | "sending" | "done" | "error">("input");

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValues({ ...values, [key]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value });

  const toConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length === 0) {
      setStep("confirm");
      window.scrollTo({ top: document.getElementById("form")?.offsetTop ?? 0, behavior: "smooth" });
    }
  };

  const send = async () => {
    setStep("sending");
    const body = new URLSearchParams();
    if (values.type === OTHER) {
      body.append(ENTRY.type, "__other_option__");
      body.append(`${ENTRY.type}.other_option_response`, values.other);
    } else {
      body.append(ENTRY.type, values.type);
    }
    body.append(ENTRY.name, values.name);
    body.append(ENTRY.email, values.email);
    body.append(ENTRY.tel, values.tel);
    body.append(ENTRY.message, values.message);
    body.append(ENTRY.agree, "同意する");
    try {
      // Googleフォームは別ドメインのため結果は読めない(no-cors)。通信できれば送信完了とする
      await fetch(SUBMIT_URL, { method: "POST", mode: "no-cors", body });
      setStep("done");
      setValues(emptyForm);
      clearDraft();
    } catch {
      setStep("error");
    }
  };

  if (step === "done") {
    return (
      <div className="cnt-done">
        <Sprout className="cnt-done-icon" />
        <h3>お問い合わせを受け付けました</h3>
        <p>内容を確認のうえ、担当者よりご連絡いたします。</p>
        <button type="button" className="tht-btn btn-orange" onClick={() => setStep("input")}>
          フォームに戻る <span>&rarr;</span>
        </button>
      </div>
    );
  }

  if (step === "confirm" || step === "sending" || step === "error") {
    const rows = [
      { label: "お問い合わせの種類", value: values.type === OTHER ? `その他(${values.other})` : values.type },
      { label: "お名前", value: values.name },
      { label: "メールアドレス", value: values.email },
      { label: "電話番号", value: values.tel },
      { label: "お問い合わせ内容", value: values.message },
    ];
    return (
      <div className="cnt-confirm">
        <p className="cnt-form-lead">以下の内容で送信します。よろしければ「送信する」を押してください。</p>
        <InfoTable rows={rows} />
        {step === "error" && (
          <p className="cnt-error cnt-error-box">送信できませんでした。時間をおいて、もう一度お試しください。</p>
        )}
        <div className="cnt-actions">
          <button type="button" className="cnt-back" onClick={() => setStep("input")} disabled={step === "sending"}>
            入力画面に戻る
          </button>
          <button type="button" className="tht-btn btn-orange" onClick={send} disabled={step === "sending"}>
            {step === "sending" ? "送信しています…" : "送信する"} <span>&rarr;</span>
          </button>
        </div>
      </div>
    );
  }

  const field = (key: keyof FormState, label: string, input: React.ReactNode) => (
    <div className={`cnt-field${errors[key] ? " has-error" : ""}`}>
      <label htmlFor={`cnt-${key}`}>
        {label}
        <span className="cnt-required">必須</span>
      </label>
      {input}
      {errors[key] && <p className="cnt-error">{errors[key]}</p>}
    </div>
  );

  return (
    <form className="cnt-form" onSubmit={toConfirm} noValidate>
      <p className="cnt-form-lead">
        フォームを入力し、「入力内容を確認」ボタンを押してください。お問い合わせ内容を確認後、担当者よりご連絡いたします。
      </p>
      <fieldset className={`cnt-field${errors.type ? " has-error" : ""}`}>
        <legend>
          お問い合わせの種類
          <span className="cnt-required">必須</span>
        </legend>
        <div className="cnt-options">
          {[...TYPES, OTHER].map((t) => (
            <label key={t} className={`cnt-option${values.type === t ? " is-checked" : ""}`}>
              <input type="radio" name="type" value={t} checked={values.type === t} onChange={set("type")} />
              <span>{t}</span>
            </label>
          ))}
        </div>
        {values.type === OTHER && (
          <input
            className="cnt-input cnt-other"
            type="text"
            placeholder="お問い合わせの種類を入力"
            value={values.other}
            onChange={set("other")}
          />
        )}
        {(errors.type || errors.other) && <p className="cnt-error">{errors.type || errors.other}</p>}
      </fieldset>
      {field("name", "お名前", <input id="cnt-name" className="cnt-input" type="text" autoComplete="name" value={values.name} onChange={set("name")} placeholder="例)山田 花子" />)}
      {field("email", "メールアドレス", <input id="cnt-email" className="cnt-input" type="email" autoComplete="email" value={values.email} onChange={set("email")} placeholder="例)example@mail.com" />)}
      {field("tel", "電話番号", <input id="cnt-tel" className="cnt-input" type="tel" autoComplete="tel" value={values.tel} onChange={set("tel")} placeholder="例)090-1234-5678" />)}
      {field("message", "お問い合わせ内容", <textarea id="cnt-message" className="cnt-input" rows={6} value={values.message} onChange={set("message")} />)}
      <div className={`cnt-field cnt-agree${errors.agree ? " has-error" : ""}`}>
        <label className={`cnt-check${values.agree ? " is-checked" : ""}`}>
          <input type="checkbox" checked={values.agree} onChange={set("agree")} />
          <span>
            <Link to="/privacypolicy">プライバシーポリシー</Link>を確認し、同意する
          </span>
        </label>
        {errors.agree && <p className="cnt-error">{errors.agree}</p>}
      </div>
      <div className="cnt-actions">
        <button type="submit" className="tht-btn btn-orange">
          入力内容を確認 <span>&rarr;</span>
        </button>
      </div>
    </form>
  );
};

// お問い合わせの内容ごとの案内(教室の申し込みは各教室ページの方が早いので、そちらへ案内する)
const guides = [
  {
    title: "教室の見学・無料体験",
    text: "各教室のページから、ご希望の教室へ直接お申し込みいただけます。",
    link: { to: "/class", label: "教室一覧へ" },
  },
  {
    title: "採用・カジュアル面談",
    text: "応募やカジュアル面談のご希望は、下のフォームからお送りください。",
    link: { to: "/company", label: "採用情報を見る" },
  },
  {
    title: "事業・取材・その他",
    text: "事業についてのご相談や取材のご依頼なども、下のフォームから受け付けています。",
  },
];

// ブラウザのタブに表示するページ名と、検索結果に出る説明
const pageMeta = {
  title: "お問い合わせ｜THT合同会社",
  description: "THT合同会社へのご質問・ご相談はこちらのフォームからお問い合わせください。",
  canonical: "https://tht-group.co.jp/contact",
};

const ThtContact = () => {
  const { hash } = useLocation();
  // プライバシーポリシーのパンくずから戻ったとき(#form 付き)は、フォームの位置まで移動する
  useEffect(() => {
    if (hash !== "#form") return;
    const timer = setTimeout(() => document.getElementById("form")?.scrollIntoView({ block: "start" }), 50);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
  <>
    <PageMeta {...pageMeta} />
    <MotionConfig reducedMotion="user">
    <div className="tht-home biz cnt">
      <section className="biz-hero cnt-hero">
        <FloatingDecor />
        {/* メッセージが届くイメージ:紙飛行機がふわりと飛んでいく */}
        <div className="cnt-plane" aria-hidden="true">
          <svg viewBox="0 0 64 40">
            <path d="M2 20 L62 2 L36 38 L28 24 Z" fill="#ffffff" stroke="#e8734a" strokeWidth="2" strokeLinejoin="round" />
            <path d="M28 24 L62 2 L22 21" fill="#fde3d2" stroke="#e8734a" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="content-container">
          <SectionHeading en="Contact" ja="お問い合わせ" />
          <Reveal delay={0.1}>
            <p className="lead-text">
              THT合同会社へのご質問・ご相談は、こちらからお気軽にお問い合わせください。
            </p>
          </Reveal>
        </div>
      </section>

      <section className="biz-section cnt-guides-section">
        <div className="content-container">
          <div className="cnt-guides">
            {guides.map((g, i) => (
              <Reveal key={g.title} delay={0.08 * i}>
                <div className="cnt-guide">
                  <h2>{g.title}</h2>
                  <p>{g.text}</p>
                  {g.link && (
                    <Link to={g.link.to} className="cnt-guide-link">
                      {g.link.label} <span>&rarr;</span>
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="form" className="biz-section cnt-form-section">
        <FloatingDecor />
        <Bubbles />
        <div className="content-container">
          <Reveal className="biz-title" x={-48} y={0}>
            <span className="biz-title-en">Form</span>
            <h2>お問い合わせフォーム</h2>
          </Reveal>
          <Reveal className="cnt-form-card">
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  </MotionConfig>
    </>
);

};

export default ThtContact;
